import React from 'react';
import { Logo } from './Shared';

/**
 * Centered card layout shared by all auth pages (login, register, forgot/reset
 * password). Keeps the visual language consistent with the rest of the app.
 */
export function AuthShell({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <div
      className="af"
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{ marginBottom: 22 }}>
        <Logo size={24} />
      </div>
      <div className="card" style={{ width: '100%', maxWidth: 380, padding: 28 }}>
        <h1 className="t-h1" style={{ margin: 0, fontSize: 22 }}>{title}</h1>
        {subtitle && <p className="muted" style={{ marginTop: 8, marginBottom: 0, fontSize: 14 }}>{subtitle}</p>}
        <div style={{ marginTop: 22 }}>{children}</div>
      </div>
      {footer && <div style={{ marginTop: 18, fontSize: 13.5 }}>{footer}</div>}
    </div>
  );
}

/** Small inline message banner used across auth forms. */
export function AuthBanner({ kind, children }: { kind: 'error' | 'success' | 'info'; children: React.ReactNode }) {
  const palette =
    kind === 'error'
      ? { bg: 'rgba(220,38,38,0.08)', fg: '#b91c1c', bd: 'rgba(220,38,38,0.25)' }
      : kind === 'success'
      ? { bg: 'rgba(22,163,74,0.08)', fg: '#15803d', bd: 'rgba(22,163,74,0.25)' }
      : { bg: 'var(--surface-2)', fg: 'var(--ink)', bd: 'var(--line)' };
  return (
    <div
      role={kind === 'error' ? 'alert' : 'status'}
      style={{
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.bd}`,
        borderRadius: 8,
        padding: '10px 12px',
        fontSize: 13.5,
        marginBottom: 16,
      }}
    >
      {children}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12.5,
  fontWeight: 600,
  color: 'var(--ink-soft, var(--ink))',
  marginBottom: 6,
};

/** Labeled text/email/password input matching the app's `.input` style. */
export function Field({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  autoComplete,
  required,
  autoFocus,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  autoFocus?: boolean;
}) {
  return (
    <label style={{ display: 'block', marginBottom: 16 }}>
      <span style={labelStyle}>{label}</span>
      <input
        className="input"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        autoFocus={autoFocus}
        style={{ width: '100%', height: 38 }}
      />
    </label>
  );
}
